import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LanguageDialog from '@/components/LanguageDialog';
import useDialog from '@/hooks/util/useDialog';
import logger from '@/logger';
import useStore from '@/store';

import useSendEvents from './util/useSendEvents';

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
];

const LANGUAGE_STORAGE_KEY = 'selectedLanguage';

function LanguageSelectedContent({
  onContinue,
  currentLanguage,
}: {
  onContinue: () => void;
  currentLanguage: string;
}) {
  const { t } = useTranslation(['translation', 'adminLogin']);

  return (
    <div className="mt-6 flex flex-col items-center text-center">
      <h2 className="mb-4 font-lilita text-[2.5rem] text-neutral-white">
        {t('adminLogin:languageSelected')}
      </h2>
      <p className="mb-8 max-w-[352px] text-center text-xl1 text-neutral-grey-8">
        {t('adminLogin:yourAppIsNowIn')}
        <span className="font-bold text-primary-yellow-4">{` ${currentLanguage}.`}</span>
        <br />
        {t('adminLogin:letsGetStarted')}
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="min-w-[190px] rounded-full bg-primary-yellow-3 px-8 py-3 font-semibold text-primary-blue-3 shadow-custom transition-colors duration-200 hover:bg-primary-yellow-4"
      >
        {t('translation:continueButton')}
      </button>
    </div>
  );
}

const useLanguage = (onDismiss?: () => void, isReloadNeeded?: boolean) => {
  const { i18n } = useTranslation();
  const { dialogProperties, updateDialogProperties, isOpen, closeDialog } = useDialog();
  const { setAppLanguage, appLanguage } = useStore();
  const [pendingLanguageCallback, setPendingLanguageCallback] = useState<string | undefined>();
  const [isApplyingLanguage, setIsApplyingLanguage] = useState(false);
  const { languageSelectedEvent } = useSendEvents();

  const getStoredLanguage = useCallback((): string | undefined => {
    try {
      return (
        JSON.parse(localStorage.getItem(LANGUAGE_STORAGE_KEY) || '{"code":"en"}') as {
          code: string;
        }
      ).code;
    } catch (error) {
      logger.warn('Failed to read from localStorage:', error);
      return undefined;
    }
  }, []);

  const setStoredLanguage = useCallback((languageCode: string): void => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, JSON.stringify({ code: languageCode }));
    } catch (error) {
      logger.warn('Failed to write to localStorage:', error);
    }
  }, []);

  const getInitialLanguage = useCallback(
    (fallback = 'en'): string => {
      const stored = getStoredLanguage();

      if (stored && AVAILABLE_LANGUAGES.some((lang) => lang.code === stored)) {
        return stored;
      }

      return fallback;
    },
    [getStoredLanguage],
  );

  useEffect(() => {
    const initialLang = getInitialLanguage();
    setAppLanguage(initialLang);
    setIsApplyingLanguage(true);

    if (i18n.language === initialLang) {
      setIsApplyingLanguage(false);
    } else {
      i18n
        .changeLanguage(initialLang)
        .then(() => {
          setIsApplyingLanguage(false);
        })
        .catch((error) => {
          logger.error('Failed to change language:', error);
          setIsApplyingLanguage(false);
        });
    }
  }, [i18n, setAppLanguage, getInitialLanguage]);

  const onLanguageSelected = useCallback(
    (currentLanguage: string) => {
      updateDialogProperties({
        open: true,
        onClose: () => {
          closeDialog();
          if (isReloadNeeded) {
            window.location.reload();
          }
        },
        component: onDismiss ? (
          <LanguageSelectedContent onContinue={onDismiss} currentLanguage={currentLanguage} />
        ) : undefined,
      });
    },
    [onDismiss, closeDialog, updateDialogProperties, isReloadNeeded],
  );

  useEffect(() => {
    if (!isOpen && pendingLanguageCallback && onLanguageSelected) {
      onLanguageSelected(pendingLanguageCallback);
      setPendingLanguageCallback(undefined);
    }
  }, [isOpen, pendingLanguageCallback, onLanguageSelected]);

  const onLanguageApply = useCallback(
    (language: Language) => {
      setAppLanguage(language.code);
      setIsApplyingLanguage(true);
      languageSelectedEvent(language.name);
      i18n
        .changeLanguage(language.code)
        .then(() => {
          onDismiss?.();
          setPendingLanguageCallback(language.name);
          setStoredLanguage(language.code);
          setIsApplyingLanguage(false);
        })
        .catch((error) => {
          logger.error('Failed to change language:', error);
          setIsApplyingLanguage(false);
        });
    },
    [i18n, onDismiss, setAppLanguage, setStoredLanguage, languageSelectedEvent],
  );

  const onLanguageCancel = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  const openLanguageDialog = useCallback(() => {
    updateDialogProperties({
      open: true,
      title: 'Choose Your Language',
      onClose: closeDialog,
      component: (
        <LanguageDialog
          onApply={onLanguageApply}
          onCancel={onLanguageCancel}
          currentLanguage={getInitialLanguage()}
          availableLanguages={AVAILABLE_LANGUAGES}
        />
      ),
    });
  }, [updateDialogProperties, onLanguageApply, onLanguageCancel, getInitialLanguage, closeDialog]);

  return {
    availableLanguages: AVAILABLE_LANGUAGES,
    openLanguageDialog,
    isLanguageDialogOpen: isOpen,
    dialogProperties,
    getInitialLanguage,
    getStoredLanguage,
    setStoredLanguage,
    appLanguage,
    isApplyingLanguage,
  };
};

export default useLanguage;
export type { Language };
